///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, October 2026
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState, useRef, useEffect, useMemo } from 'react'
import { formatNr } from '../../../services/FormatService'
import { createNotification } from '../../../services/NotificationService'
import { getDateFromTimestamp, getShortMonthName, toFormatDate, toFormatTime } from '../../../services/DateService'
import { getAllSubscriptionNames } from '../../../services/SubscriptionsService'
import {
    fetchCurrentSubscriptions,
    fetchPendingSubscriptions,
    fetchAllSubscriptions,
    fetchUnpaidInvoices,
    fetchInvoicesSince,
    fetchUsersInfo,
    classifyCurrent,
    buildOverview,
    usageHealth,
    revenueByMonth,
    renewalForecast,
    conversionStats,
    lastMonths,
    nextMonths,
    isDemo,
    isPaying,
    subMonthlyPrice,
    subContractValue,
    subStartDate } from '../../../services/AdminDashboardService'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown, faArrowsRotate, faSpinner } from '@fortawesome/free-solid-svg-icons'
import './AdminDashboard.css'

const TVA = 21 / 100
const REVENUE_MONTHS = 12

//? the datasets the sections are built from - each one is fetched once, the first time a
//? section needing it is opened, and shared by every other section that needs it
const LOADERS = {
    current: fetchCurrentSubscriptions,
    pending: fetchPendingSubscriptions,
    unpaid: fetchUnpaidInvoices,
    invoices: () => fetchInvoicesSince(lastMonths(REVENUE_MONTHS)[0].date),
    all: fetchAllSubscriptions
}

const SECTIONS = [
    { key: 'overview', title: 'Sumar', data: ['current', 'pending'],
        hint: 'Clienți plătitori, venit lunar recurent, distribuție pe pachete' },
    { key: 'renewals', title: 'Expiră în curând', data: ['current', 'pending'],
        hint: 'Abonamente care trebuie reînnoite - cine, când, ce valoare' },
    { key: 'lapsed', title: 'Expirate, nereînnoite', data: ['current', 'pending'],
        hint: 'Clienți pierduți și demo-uri neconvertite, de recontactat' },
    { key: 'unpaid', title: 'Comenzi neplătite', data: ['unpaid'],
        hint: 'Proforme emise și neachitate, cu vechimea lor' },
    { key: 'revenue', title: 'Venituri și prognoză', data: ['invoices', 'current'],
        hint: 'Încasări pe ultimele 12 luni și reînnoirile programate în următoarele 12' },
    { key: 'usage', title: 'Utilizare și risc', data: ['current'],
        hint: 'Clienți care nu folosesc aplicația (risc) sau care își depășesc pachetul (upgrade)' },
    { key: 'conversion', title: 'Conversii și retenție', data: ['all'],
        hint: 'Înscrieri, conversii demo → plătit, rata de reînnoire. Citește tot istoricul abonamentelor.' }
]

const planName = (name) => getAllSubscriptionNames()[name] || name
const eur = (value) => `${formatNr(value)} €`
const lei = (value) => `${formatNr(value)} Lei`
const pct = (ratio) => ratio === null || ratio === undefined ? '—' : `${Math.round(ratio * 100)}%`
const monthLabel = (date) => `${getShortMonthName(date.getMonth() + 1)} ${date.getFullYear()}`

//? the same three tones the rest of the panel uses for invoice states
const urgencyTone = (days) => days <= 7 ? '2' : days <= 30 ? '0' : '1'

const DaysPill = ({ days, past = false }) => {
    const label = past
        ? `acum ${-days} ${-days === 1 ? 'zi' : 'zile'}`
        : days === 0 ? 'azi' : `${days} ${days === 1 ? 'zi' : 'zile'}`
    return <span className={`ap-status ap-status--${past ? '2' : urgencyTone(days)}`}>{label}</span>
}

const Kpi = ({ label, value, sub, tone }) => (
    <div className={`apd-kpi${tone ? ` apd-kpi--${tone}` : ''}`}>
        <span className='apd-kpi-label'>{label}</span>
        <span className='apd-kpi-value'>{value}</span>
        {sub && <span className='apd-kpi-sub'>{sub}</span>}
    </div>
)

//? one magnitude bar per table row - the exact figure always sits next to it
const Bar = ({ value, max, title }) => (
    <div className='apd-bar-track' title={title}>
        <div className='apd-bar' style={{ width: max > 0 ? `${Math.max(value > 0 ? 2 : 0, value / max * 100)}%` : 0 }}/>
    </div>
)

const Segmented = ({ options, value, onChange }) => (
    <div className='apd-segmented'>
        {options.map(([optValue, label]) => (
            <div key={optValue}
                className={`apd-seg${value === optValue ? ' apd-seg--on' : ''}`}
                onClick={() => onChange(optValue)}>{label}</div>
        ))}
    </div>
)

const Empty = ({ children }) => <div className='apd-empty'>{children}</div>

const AdminDashboard = ({ onValidateSub, canValidate }) => {
    const [openSections, setOpenSections] = useState({})
    const [data, setData] = useState({})
    const [loadedAt, setLoadedAt] = useState({})
    const [loading, setLoading] = useState({})
    const [errors, setErrors] = useState({})
    const [users, setUsers] = useState({})

    // per-section filters
    const [renewWindow, setRenewWindow] = useState(30)
    const [renewKind, setRenewKind] = useState('paid')
    const [lapsedWindow, setLapsedWindow] = useState(90)
    const [lapsedKind, setLapsedKind] = useState('paid')
    const [usageFilter, setUsageFilter] = useState('risk')

    const inflight = useRef({})
    const usersRequested = useRef({})

    const loadDataset = (key, force) => {
        if (!force && inflight.current[key]) return inflight.current[key]
        const promise = LOADERS[key]()
            .then(result => {
                setData(prev => ({ ...prev, [key]: result }))
                return result
            })
            .catch(err => {
                //? forget the failed attempt so opening the section again retries it
                delete inflight.current[key]
                throw err
            })
        inflight.current[key] = promise
        return promise
    }

    const loadSection = async (section, force = false) => {
        setLoading(prev => ({ ...prev, [section.key]: true }))
        setErrors(prev => ({ ...prev, [section.key]: null }))
        try {
            await Promise.all(section.data.map(key => loadDataset(key, force)))
            setLoadedAt(prev => ({ ...prev, [section.key]: new Date() }))
        }
        catch (err) {
            console.error(err)
            setErrors(prev => ({ ...prev, [section.key]: err.message }))
            createNotification('error', `Eroare la încărcarea secțiunii "${section.title}": ${err.message}`)
        }
        finally {
            setLoading(prev => ({ ...prev, [section.key]: false }))
        }
    }

    const toggleSection = (section) => {
        const willOpen = !openSections[section.key]
        setOpenSections(prev => ({ ...prev, [section.key]: willOpen }))
        if (willOpen && !loadedAt[section.key] && !loading[section.key]) loadSection(section)
    }

    const isReady = (section) => section.data.every(key => data[key])

    //? profile lookups are only made for the rows actually on screen
    const ensureUsers = (uids) => {
        const missing = [...new Set(uids)].filter(uid => uid && !usersRequested.current[uid])
        if (missing.length === 0) return
        missing.forEach(uid => { usersRequested.current[uid] = true })
        fetchUsersInfo(missing).then(found => setUsers(prev => ({ ...prev, ...found })))
    }

    // ─── Derived data (recomputed only when its datasets change) ────────────────────────

    const { current: currentSubs, pending: pendingSubs, unpaid: unpaidInvoices,
        invoices: recentInvoices, all: allSubs } = data

    const classified = useMemo(() => currentSubs
        ? classifyCurrent(currentSubs, pendingSubs || [])
        : null, [currentSubs, pendingSubs])

    const overview = useMemo(() => currentSubs && pendingSubs
        ? buildOverview(currentSubs, pendingSubs)
        : null, [currentSubs, pendingSubs])

    const renewalRows = useMemo(() => {
        if (!classified) return []
        return classified.active
            .filter(sub => sub.daysLeft <= renewWindow)
            .filter(sub => renewKind === 'all' || (renewKind === 'paid' ? isPaying(sub) : isDemo(sub)))
            .map(sub => ({ ...sub, health: usageHealth(sub) }))
            .sort((a, b) => a.daysLeft - b.daysLeft)
    }, [classified, renewWindow, renewKind])

    const lapsedRows = useMemo(() => {
        if (!classified) return []
        return classified.lapsed
            .filter(sub => lapsedWindow === 0 || -sub.daysLeft <= lapsedWindow)
            .filter(sub => lapsedKind === 'all' || (lapsedKind === 'paid' ? isPaying(sub) : isDemo(sub)))
            .sort((a, b) => b.daysLeft - a.daysLeft)
    }, [classified, lapsedWindow, lapsedKind])

    const unpaidRows = useMemo(() => {
        if (!unpaidInvoices) return []
        const today = new Date()
        return unpaidInvoices
            .map(inv => {
                const created = getDateFromTimestamp(inv.created || inv.timestamp)
                return { ...inv, createdDate: created, age: created ? Math.floor((today - created) / 86400000) : null }
            })
            .sort((a, b) => (b.age || 0) - (a.age || 0))
    }, [unpaidInvoices])

    const revenue = useMemo(() => recentInvoices
        ? revenueByMonth(recentInvoices, lastMonths(REVENUE_MONTHS))
        : null, [recentInvoices])

    const forecast = useMemo(() => currentSubs
        ? renewalForecast(currentSubs, nextMonths(12))
        : null, [currentSubs])

    const usageRows = useMemo(() => {
        if (!classified) return []
        return classified.active
            .filter(isPaying)
            .map(sub => ({ ...sub, health: usageHealth(sub) }))
            .filter(sub => usageFilter === 'all' || sub.health.flag === usageFilter)
            .sort((a, b) => usageFilter === 'upsell'
                ? (b.health.usage?.exhaustedMonths || 0) - (a.health.usage?.exhaustedMonths || 0)
                : a.daysLeft - b.daysLeft)
    }, [classified, usageFilter])

    const usageCounts = useMemo(() => {
        if (!classified) return { risk: 0, upsell: 0 }
        const flags = classified.active.filter(isPaying).map(sub => usageHealth(sub).flag)
        return { risk: flags.filter(f => f === 'risk').length, upsell: flags.filter(f => f === 'upsell').length }
    }, [classified])

    const conversion = useMemo(() => allSubs ? conversionStats(allSubs, 12) : null, [allSubs])

    // fetch the profiles for whatever the open sections are listing
    useEffect(() => {
        const uids = []
        if (openSections.renewals) renewalRows.forEach(sub => uids.push(sub.uid))
        if (openSections.lapsed) lapsedRows.forEach(sub => uids.push(sub.uid))
        if (openSections.usage) usageRows.forEach(sub => uids.push(sub.uid))
        ensureUsers(uids)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openSections, renewalRows, lapsedRows, usageRows])

    // ─── Cells shared by the lists ─────────────────────────────────────────────────────

    const clientCell = (uid) => {
        const info = users[uid]
        if (!info) return <td><span className='ap-mono'>{uid}</span>{info === undefined && <span className='apd-muted'> …</span>}</td>
        return (
            <td>
                <div className='apd-client'>
                    <span className='apd-client-name'>{info.company || info.name || '—'}</span>
                    <span className='apd-muted'>{info.email}{info.phone ? ` · ${info.phone}` : ''}</span>
                    <span className='ap-mono apd-muted'>{uid}</span>
                </div>
            </td>
        )
    }

    const pendingTag = (sub) => sub.pending
        ? <span className='apd-tag apd-tag--ok' title={`Comandă ${planName(sub.pending.name)} plasată, neplătită`}>
            comandă plasată
          </span>
        : null

    // ─── Section bodies ────────────────────────────────────────────────────────────────

    const renderOverview = () => {
        const o = overview
        const maxPlan = Math.max(...o.byPlan.map(p => p.count), 0)
        return (
            <>
                <div className='apd-kpis'>
                    <Kpi label='Clienți plătitori activi' value={o.payingCount}
                        sub={`medie ${eur(o.avgMonthly)} / lună`}/>
                    <Kpi label='Venit lunar recurent' value={eur(o.mrr)}
                        sub={`≈ ${eur(o.arr)} / an, fără TVA`}/>
                    <Kpi label='Expiră în 7 zile' value={o.expiring7} tone={o.expiring7 ? 'bad' : null}
                        sub='abonamente plătite'/>
                    <Kpi label='Expiră în 30 zile' value={o.expiring30} tone={o.expiring30 ? 'warn' : null}
                        sub={`${eur(o.expiring30Value)} de reînnoit`}/>
                    <Kpi label='Expiră în 90 zile' value={o.expiring90}
                        sub={`${eur(o.expiring90Value)} de reînnoit`}/>
                    <Kpi label='Pierduți ultimele 30 zile' value={o.lapsed30} tone={o.lapsed30 ? 'bad' : null}
                        sub={`${eur(o.lapsed30Mrr)} / lună pierdut · ${o.lapsed90} în 90 zile`}/>
                    <Kpi label='Comenzi neplătite' value={o.pendingCount} tone={o.pendingCount ? 'warn' : null}
                        sub={`${eur(o.pendingValue)} în așteptare`}/>
                    <Kpi label='Demo active' value={o.demoActive}
                        sub={`${o.demoExpiring14} expiră în 14 zile${o.freeActive ? ` · ${o.freeActive} gratuite` : ''}`}/>
                </div>

                <span className='apd-subtitle'>Abonamente active pe pachete</span>
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Pachet</th><th>Active</th><th className='apd-bar-col'></th><th>Venit lunar</th></tr>
                        </thead>
                        <tbody>
                            {o.byPlan.map(plan => (
                                <tr key={plan.name}>
                                    <td>{planName(plan.name)}</td>
                                    <td>{plan.count}</td>
                                    <td><Bar value={plan.count} max={maxPlan} title={`${planName(plan.name)}: ${plan.count} active`}/></td>
                                    <td>{plan.mrr ? eur(plan.mrr) : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        )
    }

    const renderRenewals = () => {
        const total = renewalRows.reduce((acc, sub) => acc + subContractValue(sub), 0)
        const ordered = renewalRows.filter(sub => sub.pending).length
        return (
            <>
                <div className='apd-toolbar'>
                    <Segmented value={renewWindow} onChange={setRenewWindow}
                        options={[[7, '7 zile'], [14, '14 zile'], [30, '30 zile'], [60, '60 zile'], [90, '90 zile']]}/>
                    <Segmented value={renewKind} onChange={setRenewKind}
                        options={[['paid', 'Plătite'], ['demo', 'Demo'], ['all', 'Toate']]}/>
                    <span className='apd-summary'>
                        <b>{renewalRows.length}</b> abonamente
                        {renewKind !== 'demo' && <> · <b>{eur(total)}</b> de reînnoit</>}
                        {ordered > 0 && <> · <b>{ordered}</b> au plasat deja comanda</>}
                    </span>
                </div>
                {renewalRows.length === 0 ? <Empty>Nimic nu expiră în intervalul ales.</Empty> :
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr>
                                <th>Expiră</th><th>Data</th><th>Client</th><th>Pachet</th>
                                <th>€ / lună</th><th>Valoare reînnoire</th><th>Utilizare medie</th><th>Stare</th>
                            </tr>
                        </thead>
                        <tbody>
                            {renewalRows.map(sub => (
                                <tr key={sub.subId}>
                                    <td><DaysPill days={sub.daysLeft}/></td>
                                    <td>{toFormatDate(sub.expiry)}</td>
                                    {clientCell(sub.uid)}
                                    <td>{planName(sub.name)} <span className='apd-muted'>· {sub.duration} luni</span></td>
                                    <td>{isDemo(sub) ? '—' : eur(subMonthlyPrice(sub))}</td>
                                    <td>{isDemo(sub) ? '—' : eur(subContractValue(sub))}</td>
                                    <td>{sub.health.avgPct !== null
                                        ? `${sub.health.avgPct}%`
                                        : sub.health.currentPct !== null ? `${sub.health.currentPct}% (luna curentă)` : '—'}</td>
                                    <td>
                                        {pendingTag(sub)}
                                        {sub.health.flag === 'risk' && <span className='apd-tag apd-tag--bad'>neutilizat</span>}
                                        {sub.health.flag === 'upsell' && <span className='apd-tag'>pachet depășit</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
            </>
        )
    }

    const renderLapsed = () => {
        const lostMrr = lapsedRows.filter(isPaying).reduce((acc, sub) => acc + subMonthlyPrice(sub), 0)
        return (
            <>
                <div className='apd-toolbar'>
                    <Segmented value={lapsedWindow} onChange={setLapsedWindow}
                        options={[[30, '30 zile'], [90, '90 zile'], [180, '6 luni'], [365, '1 an'], [0, 'Tot']]}/>
                    <Segmented value={lapsedKind} onChange={setLapsedKind}
                        options={[['paid', 'Plătite'], ['demo', 'Demo neconvertite'], ['all', 'Toate']]}/>
                    <span className='apd-summary'>
                        <b>{lapsedRows.length}</b> clienți
                        {lapsedKind !== 'demo' && <> · <b>{eur(lostMrr)}</b> / lună pierdut</>}
                    </span>
                </div>
                {lapsedRows.length === 0 ? <Empty>Niciun abonament expirat nereînnoit în intervalul ales.</Empty> :
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Expirat</th><th>Data</th><th>Client</th><th>Ultimul pachet</th><th>€ / lună</th><th>Client din</th><th>Stare</th></tr>
                        </thead>
                        <tbody>
                            {lapsedRows.map(sub => (
                                <tr key={sub.subId}>
                                    <td><DaysPill days={sub.daysLeft} past/></td>
                                    <td>{toFormatDate(sub.expiry)}</td>
                                    {clientCell(sub.uid)}
                                    <td>{planName(sub.name)} <span className='apd-muted'>· {sub.duration} luni</span></td>
                                    <td>{isDemo(sub) ? '—' : eur(subMonthlyPrice(sub))}</td>
                                    <td>{toFormatDate(subStartDate(sub))}</td>
                                    <td>{pendingTag(sub)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
            </>
        )
    }

    const renderUnpaid = () => {
        const total = unpaidRows.reduce((acc, inv) => acc + (parseFloat(inv.price) || 0), 0)
        const stale = unpaidRows.filter(inv => inv.age > 14).length
        return (
            <>
                <div className='apd-toolbar'>
                    <span className='apd-summary'>
                        <b>{unpaidRows.length}</b> proforme · <b>{lei(total * (1 + TVA))}</b> cu TVA
                        ({lei(total)} fără TVA)
                        {stale > 0 && <> · <b>{stale}</b> mai vechi de 14 zile</>}
                    </span>
                </div>
                {unpaidRows.length === 0 ? <Empty>Nicio comandă neplătită.</Empty> :
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Vechime</th><th>Proformă</th><th>Emisă</th><th>Client</th><th>Pachet</th><th>Total cu TVA</th><th>ID abonament</th>{canValidate && <th></th>}</tr>
                        </thead>
                        <tbody>
                            {unpaidRows.map(inv => (
                                <tr key={inv.id}>
                                    <td>{inv.age === null ? '—'
                                        : <span className={`ap-status ap-status--${inv.age > 14 ? '2' : inv.age > 3 ? '0' : '1'}`}>
                                            {inv.age === 0 ? 'azi' : `${inv.age} ${inv.age === 1 ? 'zi' : 'zile'}`}
                                          </span>}
                                    </td>
                                    <td>{inv.seriesNumber}</td>
                                    <td>{inv.createdDate ? `${toFormatDate(inv.createdDate)} ${toFormatTime(inv.createdDate)}` : '—'}</td>
                                    <td>
                                        <div className='apd-client'>
                                            <span className='apd-client-name'>{inv.client?.name || '—'}</span>
                                            <span className='apd-muted'>{inv.client?.email}</span>
                                        </div>
                                    </td>
                                    <td>{inv.subscription?.name} <span className='apd-muted'>· {inv.subscription?.period} luni</span></td>
                                    <td>{lei((parseFloat(inv.price) || 0) * (1 + TVA))}</td>
                                    <td className='ap-mono'>{inv.subscription?.id}</td>
                                    {canValidate &&
                                        <td>
                                            {inv.subscription?.id &&
                                                <div className='button-secondary apd-row-btn'
                                                    onClick={() => onValidateSub(inv.subscription.id)}>Stare</div>}
                                        </td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>}
            </>
        )
    }

    const renderRevenue = () => {
        const maxPaid = Math.max(...revenue.map(m => m.paid), 0)
        const maxForecast = Math.max(...forecast.map(m => m.value), 0)
        const totals = revenue.reduce((acc, m) => ({
            paid: acc.paid + m.paid, paidCount: acc.paidCount + m.paidCount,
            canceledCount: acc.canceledCount + m.canceledCount
        }), { paid: 0, paidCount: 0, canceledCount: 0 })
        const forecastTotal = forecast.reduce((acc, m) => acc + m.value, 0)
        return (
            <>
                <span className='apd-subtitle'>Încasări - ultimele 12 luni (Lei, fără TVA, după data plății)</span>
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Luna</th><th>Facturi achitate</th><th className='apd-bar-col'></th><th>Încasat</th><th>Neachitate</th><th>Anulate</th></tr>
                        </thead>
                        <tbody>
                            {revenue.map(m => (
                                <tr key={m.key}>
                                    <td>{monthLabel(m.date)}</td>
                                    <td>{m.paidCount}</td>
                                    <td><Bar value={m.paid} max={maxPaid} title={`${monthLabel(m.date)}: ${lei(m.paid)} din ${m.paidCount} facturi`}/></td>
                                    <td>{lei(m.paid)}</td>
                                    <td>{m.unpaidCount ? `${m.unpaidCount} · ${lei(m.unpaid)}` : '—'}</td>
                                    <td>{m.canceledCount || '—'}</td>
                                </tr>
                            ))}
                            <tr className='apd-total-row'>
                                <td>Total</td><td>{totals.paidCount}</td><td></td>
                                <td>{lei(totals.paid)}</td><td></td><td>{totals.canceledCount || '—'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <span className='apd-subtitle'>Reînnoiri programate - următoarele 12 luni (€, fără TVA, dacă toți reînnoiesc)</span>
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Luna</th><th>Abonamente plătite care expiră</th><th className='apd-bar-col'></th><th>Valoare</th><th>Demo care expiră</th></tr>
                        </thead>
                        <tbody>
                            {forecast.map(m => (
                                <tr key={m.key}>
                                    <td>{monthLabel(m.date)}</td>
                                    <td>{m.count}</td>
                                    <td><Bar value={m.value} max={maxForecast} title={`${monthLabel(m.date)}: ${m.count} abonamente, ${eur(m.value)}`}/></td>
                                    <td>{m.value ? eur(m.value) : '—'}</td>
                                    <td>{m.demoCount || '—'}</td>
                                </tr>
                            ))}
                            <tr className='apd-total-row'>
                                <td>Total</td><td>{forecast.reduce((acc, m) => acc + m.count, 0)}</td><td></td>
                                <td>{eur(forecastTotal)}</td><td>{forecast.reduce((acc, m) => acc + m.demoCount, 0) || '—'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </>
        )
    }

    const renderUsage = () => (
        <>
            <div className='apd-toolbar'>
                <Segmented value={usageFilter} onChange={setUsageFilter}
                    options={[
                        ['risk', `Risc de abandon (${usageCounts.risk})`],
                        ['upsell', `Candidați upgrade (${usageCounts.upsell})`],
                        ['all', 'Toți plătitorii']
                    ]}/>
            </div>
            <div className='apd-note'>
                {usageFilter === 'risk' && 'Plătitori care nu au folosit nicio linie nici luna aceasta, nici luna trecută - puțin probabil să reînnoiască fără un contact.'}
                {usageFilter === 'upsell' && 'Plătitori care și-au epuizat liniile într-o lună sau au folosit deja peste 90% din luna curentă.'}
                {usageFilter === 'all' && 'Toți clienții plătitori activi, cu consumul lor de linii.'}
            </div>
            {usageRows.length === 0 ? <Empty>Niciun client în această categorie.</Empty> :
            <div className='ap-table-wrap'>
                <table className='ap-table'>
                    <thead>
                        <tr><th>Client</th><th>Pachet</th><th>Luna curentă</th><th>Medie / lună</th><th>Vârf</th><th>Luni epuizate</th><th>Expiră</th></tr>
                    </thead>
                    <tbody>
                        {usageRows.map(sub => {
                            const usage = sub.health.usage
                            return (
                                <tr key={sub.subId}>
                                    {clientCell(sub.uid)}
                                    <td>{planName(sub.name)}</td>
                                    <td>{usage ? `${formatNr(usage.current.used)} / ${formatNr(usage.allowance)} (${sub.health.currentPct}%)` : '—'}</td>
                                    <td>{usage && usage.averageUsed !== null ? `${formatNr(usage.averageUsed)} (${sub.health.avgPct}%)` : '—'}</td>
                                    <td>{usage ? formatNr(usage.peakUsed) : '—'}</td>
                                    <td>{usage ? `${usage.exhaustedMonths} / ${usage.completedMonths}` : '—'}</td>
                                    <td><DaysPill days={sub.daysLeft}/></td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>}
        </>
    )

    const renderConversion = () => {
        const c = conversion
        const maxSignups = Math.max(...c.rows.map(r => r.signups), 0)
        return (
            <>
                <div className='apd-kpis'>
                    <Kpi label='Conturi totale' value={c.totalUsers}/>
                    <Kpi label='Înscrieri 12 luni' value={c.totalSignups}/>
                    <Kpi label='Conversie demo → plătit' value={pct(c.conversionRate)}
                        sub='din înscrierile ultimelor 12 luni'/>
                    <Kpi label='Timp median până la plată' value={c.medianDaysToConvert === null ? '—' : `${c.medianDaysToConvert} zile`}/>
                    <Kpi label='Rată de reînnoire' value={pct(c.renewalRate)}
                        tone={c.renewalRate !== null && c.renewalRate < 0.5 ? 'warn' : null}
                        sub={`${c.renewDone} din ${c.renewDue} abonamente expirate în 12 luni`}/>
                </div>
                <div className='ap-table-wrap'>
                    <table className='ap-table'>
                        <thead>
                            <tr><th>Luna</th><th>Înscrieri</th><th className='apd-bar-col'></th><th>Convertite (până azi)</th><th>Prime achiziții</th><th>Reînnoiri / upgrade</th></tr>
                        </thead>
                        <tbody>
                            {c.rows.map(r => (
                                <tr key={r.key}>
                                    <td>{monthLabel(r.date)}</td>
                                    <td>{r.signups}</td>
                                    <td><Bar value={r.signups} max={maxSignups} title={`${monthLabel(r.date)}: ${r.signups} înscrieri`}/></td>
                                    <td>{r.signups ? `${r.converted} (${pct(r.converted / r.signups)})` : '—'}</td>
                                    <td>{r.firstPurchases || '—'}</td>
                                    <td>{r.renewals || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        )
    }

    const RENDERERS = {
        overview: renderOverview,
        renewals: renderRenewals,
        lapsed: renderLapsed,
        unpaid: renderUnpaid,
        revenue: renderRevenue,
        usage: renderUsage,
        conversion: renderConversion
    }

    return (
        <div className='ap-card'>
            <span className='ap-card-label'>Dashboard abonamente și clienți</span>
            <div className='apd-sections'>
                {SECTIONS.map(section => {
                    const isOpen = !!openSections[section.key]
                    const isLoading = !!loading[section.key]
                    return (
                        <div key={section.key} className={`apd-section${isOpen ? ' apd-section--open' : ''}`}>
                            <div className='apd-section-head' onClick={() => toggleSection(section)}>
                                <FontAwesomeIcon className='apd-chevron' icon={isOpen ? faChevronDown : faChevronRight}/>
                                <div className='apd-section-titles'>
                                    <span className='apd-section-title'>{section.title}</span>
                                    <span className='apd-section-hint'>{section.hint}</span>
                                </div>
                                {isLoading && <FontAwesomeIcon className='spinner' icon={faSpinner}/>}
                            </div>
                            {isOpen &&
                                <div className='apd-section-body'>
                                    {(loadedAt[section.key] || errors[section.key]) &&
                                        <div className='apd-refresh'>
                                            <span>{loadedAt[section.key]
                                                ? `Actualizat la ${toFormatTime(loadedAt[section.key])}`
                                                : 'Încărcarea a eșuat'}</span>
                                            <FontAwesomeIcon className={`apd-refresh-btn${isLoading ? ' spinner' : ''}`}
                                                title='Reîncarcă' icon={isLoading ? faSpinner : faArrowsRotate}
                                                onClick={() => !isLoading && loadSection(section, true)}/>
                                        </div>
                                    }
                                    {errors[section.key] && <div className='ap-popup-warning ap-popup-warning--error'>{errors[section.key]}</div>}
                                    {isReady(section) && loadedAt[section.key]
                                        ? RENDERERS[section.key]()
                                        : !errors[section.key] && <Empty>Se încarcă…</Empty>}
                                </div>
                            }
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default AdminDashboard
